<template>
  <div
    v-if="!isSkipModuleEnable"
    ref="target"
    :class="['dn-live-wrapped-ad', minHeight !== 1 || adsHeight > 1 ? 'view-ads' : 'hide-ads', stickyHideClass]"
    :style="[!isAdGroupChild && { minHeight: `${minHeight !== 1 ? minHeight : adsHeight}px` }]"
  >
    <div
      :data-info="(minHeight !== 1 || adsHeight > 1) && label && label.length > 0 ? label : null"
      :class="'dfp-ad'"
    >
      <div
        :id="id"
        :class="{ 'ad-group-child': isAdGroupChild }"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import debounce from 'lodash.debounce';
import { onMounted, ref } from 'vue';
import type { ElementPositionPayload } from '~/types/elementPositionPayload';
const props = defineProps({
  id: {
    type: String,
    default: null,
    required: true
  },
  minHeight: {
    type: Number,
    required: false,
    default: 1
  },
  label: {
    type: String,
    default: null,
    required: false
  },
  shouldRemoveWallpaperStyle: {
    type: Boolean,
    default: false,
    required: false
  },
  shouldHideOrStop: {
    type: Boolean,
    default: false,
    required: false
  },
  isAdGroupChild: {
    type: Boolean,
    default: false,
    required: false
  }
});

const route = useRoute();
const { skipModule, moduleNames } = useSkipModules(route);
const isSkipModuleEnable = ref(skipModule(moduleNames.AD));
const target = ref(null);
const adsHeight = ref(1);
const stickyHidingElementsMapper = ref({});
const nuxtApp = useNuxtApp();

let listenerCleanup: (() => void) | null = null;

if (props.minHeight === 1) {
  const debounceSetAdHeight = debounce((height: number) => {
    if (height > 1) {
      adsHeight.value = height;
    }
    if (height === 0) {
      adsHeight.value = 1;
    }
  }, 500);

  useResizeObserver(target, (entries) => {
    const entry = entries[0];
    const { height } = entry.contentRect;
    debounceSetAdHeight(height);
  });
}

onMounted(() => {
  if (props.shouldRemoveWallpaperStyle) {
    removeWallpaperStyle();
  }
  loadAd(props.id);
  if (props.shouldHideOrStop) {
    listenHideOrStopEvent();
  }
});

/*
 * This function will be removed in future whenever we will introduce slot for
 * wallpaper and css will be inside CPP
 */
function removeWallpaperStyle () {
  const wpStyle = document.head.querySelector('#dn-wallpaperad');
  if (wpStyle) {
    wpStyle.remove();
  }
}

/*
 * Returns true when the ad server has hidden the slot (display:none on the
 * inner div).
 */
function isAdSlotHidden (el: HTMLElement): boolean {
  const adSlot = el.querySelector<HTMLElement>(':scope > .dfp-ad > div');
  return !!adSlot && getComputedStyle(adSlot).display === 'none';
}

/*
 * Calculates how tall the sticky ad should be when a colliding element stops
 * it.
 */
function calcStickyStopHeight (el: HTMLElement, enteringElement: HTMLElement): number {
  const stickyTopPosition = parseFloat(document.documentElement.style.getPropertyValue('--sticky-top-position'));
  let height = enteringElement.offsetTop - stickyTopPosition;

  const isRightOrLeftSticky = el.classList.contains('right-sticky') || el.classList.contains('left-sticky');
  if (isRightOrLeftSticky) {
    const stickyContainer = document.querySelector<HTMLElement>('.right-sticky-container, .left-sticky-container');
    if (stickyContainer) {
      height -= parseFloat(getComputedStyle(stickyContainer).top);
    }
  }

  return height;
}

function listenHideOrStopEvent (): void {
  listenerCleanup = useBusListener({
    'element-position': (payload: ElementPositionPayload) => {
      const el = target.value as HTMLElement | null;
      if (!el || !payload.position || !payload.key) {
        return;
      }
      if (isAdSlotHidden(el)) {
        return;
      }

      const { element: enteringElement, action: elementAction, isIntersecting, position: enteringElementPosition } = payload;
      const elementPosition = el.getBoundingClientRect();

      if (isColliding(elementPosition, enteringElementPosition) && isIntersecting) {
        if (containsString(elementAction, 'sticky-stop')) {
          setStickyHeight(calcStickyStopHeight(el, enteringElement));
        } else {
          setStickyHeight(0);
          setStickyHidingElementsMapper(payload.key, true);
        }
      } else {
        setStickyHidingElementsMapper(payload.key, false);
      }
    }
  });
}

onUnmounted(() => {
  listenerCleanup?.();
});

function isColliding (rectInViewPort: DOMRect, rectIntersecting: DOMRect): boolean {
  if (rectIntersecting.height === 0 || rectInViewPort.height === 0) {
    return false;
  }
  return (
    rectInViewPort.left < rectIntersecting.right &&
    rectInViewPort.right > rectIntersecting.left &&
    rectInViewPort.top - 100 < rectIntersecting.bottom &&
    rectInViewPort.bottom + 100 > rectIntersecting.top
  );
}

function containsString (action: string | string[], checkString: string): boolean {
  if (typeof action === 'string') {
    return action === checkString;
  }
  return action.includes(checkString);
}
// Use the computed function to create a computed property
const stickyHideClass = computed(() => {
  // Check is it sticky ads or there any sticky height set
  const mappedValues = Object.values(stickyHidingElementsMapper.value);
  return mappedValues.length > 0 && mappedValues.includes(true) ? 'ad-sticky-hide' : '';
});
function setStickyHidingElementsMapper (key: string, isIntersecting: boolean) {
  const keySlug = key.toLowerCase().replace('/s/g', '-');
  // @ts-ignore
  stickyHidingElementsMapper.value[keySlug] = isIntersecting;
}
function clearStickyHidingStopElementsMapper () {
  stickyHidingElementsMapper.value = {};
  setStickyHeight(0);
}
if (import.meta.client) {
  nuxtApp.hook('cpp:data:loaded', () => {
    clearStickyHidingStopElementsMapper();
  });
}
</script>

<style lang="scss">
%wallpaperAdBoxLayout {
  @media only screen and (width >= 1328px) {
    inline-size: 1328px;
    padding-left: var(--site-padding);
    padding-right: var(--site-padding);
    margin: 0 auto;
  }
}

.top-ad-placeholder {
  position: relative;
  z-index: 8;

  .is-flyout-open & {
    z-index: 0;
  }

  .dn-frontpage &,
  .dn-topic &,
  .dn-tag &,
  .dn-latest &,
  .dn-search &,
  .dn-category & {
    @media only screen and (width >= 1328px) {
      grid-area: 2 / 2 / 1 / 4;
    }
  }

  &:has(.dn-wallpaperad-bg) {
    + .dn-article {
      .dn-article-top {
        position: relative;
        inline-size: min(var(--article-width), 100vw - var(--site-padding) * 2);
        background-color: var(--color-page-bg);
        z-index: 8;
        overflow: visible;

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          display: block;
          width: var(--row-gap);
          height: 100%;
          background: var(--color-page-bg);
        }

        &::before {
          left: calc(var(--row-gap) * -1);
        }

        &::after {
          right: calc(var(--row-gap) * -1);
        }
      }
    }

    ~ .atlas-header {
      &:not(.is-sticky) {
        @extend %wallpaperAdBoxLayout;

        padding: 0;
        transition: none;
      }
    }

    ~ .dn-navigation {
      @extend %wallpaperAdBoxLayout;
    }

    ~ .dn-ticker {
      @extend %wallpaperAdBoxLayout;
    }

    ~ .dn-page-layout {
      .dn-frontpage,
      .dn-tag,
      .dn-topic,
      .dn-latest,
      .dn-search,
      .dn-category {
        @extend %wallpaperAdBoxLayout;

        position: relative;
        background: var(--color-page-bg);
        z-index: 8;
      }
    }

    .dn-frontpage &,
    .dn-topic &,
    .dn-tag &,
    .dn-latest &,
    .dn-search &,
    .dn-category & {
      inline-size: 100vw;
      margin-left: calc(var(--row-gap) * -2);

      @media only screen and (width >= 1328px) {
        margin-left: calc(-50vw + var(--tag-topic-category-top-content-width) / 2);

        .top-ad-below-header {
          width: 980px;
          padding: 0;
          margin: 0 auto;
        }
      }

      ~ .left-column,
      ~ .right-column {
        top: 1104px;
        height: calc(100% - 1104px);
      }

      ~ .dn-edition-collection-top,
      ~ .dn-collection-top {
        position: relative;
        background-color: var(--color-page-bg);
        z-index: 9;

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          display: block;
          width: var(--row-gap);
          height: 780px;
          background-color: var(--color-page-bg);
        }

        &::before {
          left: calc(var(--row-gap) * -1);
        }

        &::after {
          right: calc(var(--row-gap) * -1);
        }
      }
    }

    .article & {
      .top-ad-below-header.view-ads {
        width: 980px;
        padding: 0;
        margin: 0 auto;
      }
    }
  }

  &:has(.top-ad-below-header.view-ads) {
    &:not(.adnm-topscroll-parent) {
      + .dn-article {
        .dn-ma-frame-top {
          margin-top: 0;
        }
      }
    }
  }
}

.top-ad-placeholder-mobile {
  background-color: var(--color-advertising-banner-bg);

  &:has(.hide-ads) {
    background: none;
  }

  &:has(.view-ads) {
    margin-bottom: 32px;
  }
}

.dn-live-wrapped-ad {
  --_dn-live-wrapped-ad_width: var(--dn-live-wrapped-ad_width, 100vw);
  --_dn-live-wrapped-ad_padding-multiply: var(--dn-live-wrapped-ad_padding-multiply, 2);

  overflow: hidden;
  transition: all 1s ease-in-out;

  .dn-frontpage & {
    @media only screen and (width < 768px) {
      inline-size: calc(var(--_dn-live-wrapped-ad_width) - var(--site-padding) * var(--_dn-live-wrapped-ad_padding-multiply));
    }
  }

  .top-ad-placeholder & {
    position: relative;
    z-index: 1;
  }

  &.hide-ads {
    opacity: 0;
    margin-top: -1px !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
  }

  &.mag-stripe {
    .dfp-ad {
      line-height: 0;

      ::before {
        display: none;
      }
    }
  }

  &.view-ads {
    &:has(.flyingCarpetHolder, .adnm-interscroll-parent, .butterflyHolder, .high-impact-ad-wrapper-midscroll, .real-estate-carousel) {
      position: relative;
      z-index: 11;
    }
  }

  &.view-ads .dfp-ad {
    &::before {
      content: attr(data-info);
      display: block;
      font-family: var(--typography-utility-s-font-family);
      font-size: var(--typography-utility-s-font-size);
      line-height: var(--typography-utility-s-line-height);
      letter-spacing: var(--typography-utility-s-letter-spacing);
      color: var(--color-typography-secondary);
      text-align: center;

      .top-ad-placeholder &,
      .left-sticky-container &,
      .right-sticky-container &,
      .right-column & {
        display: none;
      }

      .top-ad-placeholder-mobile & {
        display: block;
      }
    }

    &[data-info] {
      &::before {
        padding: 8px 0;
      }
    }
  }

  &.full-width {
    grid-column: span 12;
  }

  &.full {
    grid-area: top;
    position: relative;
    width: 100%;
    transition: opacity 0.3s ease;
    box-sizing: content-box;

    &:has(.job_carousel) {
      inline-size: min(var(--content-width), 100vw - var(--site-padding) * 2);
      margin: 0 auto var(--row-gap-article);
      background: none;
    }

    &:not(.top-ad-below-header) {
      background: var(--color-advertising-banner-bg);
    }

    &:not(.top-ad-below-header, .top-ad-above-header),
    .top-ad-placeholder-mobile & {
      display: flex;
      justify-content: center;
      padding: 24px 0;

      @media only screen and (width < 768px) {
        .dfp-ad {
          width: 100%;
        }
      }
    }

    @media only screen and (width >= 768px) {
      &:not(.top-ad-below-header).view-ads {
        padding-bottom: 32px;
        padding-top: 24px;
        display: block;
      }

      &.top-ad-above-header {
        &:has(.dn-wallpaperad-bg) {
          overflow: visible;
          padding: 0;
        }
      }
    }

    &.no-background {
      background: none;
    }
  }

  &:has(.dfp-ad[style='display: none;']) {
    display: none;
  }

  .fullscreen {
    position: relative;
    width: 100%;
    background: var(--color-advertising-banner-bg);
    padding: 0;
    transition: opacity 0.3s ease;
    box-sizing: content-box;
  }

  &.follow-grid {
    width: 100%;
    margin: 0 auto;
  }

  &.left-sticky {
    display: none;
    transition: all 0.3s ease;
    overflow: visible;

    @media only screen and (width >= 1328px) {
      grid-area: ad-left;
      position: sticky;
      display: flex;
      justify-content: flex-end;
      width: auto;
      top: calc(var(--row-gap) * 4);
      left: var(--ui-gap);
      justify-self: end;
      z-index: 8;
    }

    &.dfp-ad::before {
      display: none;
    }
  }

  &.right-sticky {
    display: flex;
    position: sticky;
    top: calc(var(--row-gap) * 4);
    justify-content: flex-start;
    width: auto;
    justify-self: start;
    z-index: 8;
    transition: all 0.3s ease;
    overflow: visible;

    &.dfp-ad::before {
      display: none;
    }
  }

  &.ad-sticky-hide {
    opacity: 0;
    visibility: hidden;
    z-index: 0;
  }

  &.bgAdFiftyFifty {
    background: var(--color-advertising-banner-bg);

    &.view-ads .dfp-ad::before {
      display: none;
    }
  }
}

.left-sticky-container,
.right-sticky-container {
  height: 100%;
  display: none;
  transition: all 0.3s ease;

  @media only screen and (width >= 1658px) {
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: auto;
    padding-top: 12px;
    margin-block-start: 12px;
    padding-bottom: var(--row-gap);
    justify-self: start;
    z-index: 11;
    top: var(--sticky-top-position);
    height: calc(100% - var(--sticky-top-position));
  }

  > :not(:nth-child(1 of .view-ads)) {
    margin-top: var(--row-gap);
  }
}

.left-sticky-container {
  @media only screen and (width >= 1658px) {
    grid-area: ad-left;
    right: 0;
    padding-right: 8px;
  }
}

.right-sticky-container {
  @media only screen and (width >= 1658px) {
    grid-area: ad-right;
    padding-left: 8px;
  }
}
</style>
